import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import StateBlock from '@/components/base/StateBlock';
import { newsItems } from '@/mocks/news';
import { sortNews } from '@/utils/content';

const PAGE_SIZE = 4;

export default function NewsCategory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page')) || 1;
  const [sortBy, setSortBy] = useState<'time' | 'hot'>('time');

  useDocumentMeta({
    title: '赛事资讯 - 51每日大赛',
    description:
      '51每日大赛赛事资讯频道，汇总赛事动态、排名分析与选手表现报道，支持按发布时间或热门程度排序，及时掌握比赛动态。',
    keywords: '赛事资讯,赛事动态,排名分析,选手表现,51每日大赛,比赛新闻',
  });

  const sorted = useMemo(() => sortNews(newsItems, sortBy), [sortBy]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(pageParam, 1), totalPages);
  const pageItems = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) {
      next.delete('page');
    } else {
      next.set('page', String(page));
    }
    setSearchParams(next);
  };

  const sortOptions: { key: 'time' | 'hot'; label: string }[] = [
    { key: 'time', label: '按发布时间' },
    { key: 'hot', label: '按热门程度' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '赛事资讯' }]} />

      <header className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-foreground-950 tracking-tight">
            赛事资讯
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
            完整的赛事相关新闻与分析报道，覆盖赛事动态、排名解析与选手表现，帮助你全面掌握比赛节奏。
            <span className="ml-1 text-foreground-400">演示内容，实际数据接入待开发。</span>
          </p>
        </div>
        <div className="inline-flex bg-background-200 p-1 rounded-full shrink-0 self-start md:self-auto">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => setSortBy(option.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                sortBy === option.key
                  ? 'bg-background-50 text-primary-700'
                  : 'text-foreground-600 hover:text-foreground-800'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      <div className="mt-8">
        {pageItems.length > 0 ? (
          <>
            <div className="flex flex-col gap-4">
              {pageItems.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>

            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => goPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="px-4 py-2 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line align-middle"></i>
                上一页
              </button>
              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    type="button"
                    onClick={() => goPage(page)}
                    className={`w-10 h-10 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      page === currentPage
                        ? 'bg-primary-600 text-background-50'
                        : 'bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => goPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="px-4 py-2 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                下一页
                <i className="ri-arrow-right-s-line align-middle"></i>
              </button>
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50">
            <StateBlock
              type="empty"
              title="暂无相关新闻内容"
              description="为你推荐以下热门赛事资讯，或返回资讯首页浏览更多内容。"
            />
          </div>
        )}
      </div>

      <div className="mt-10 rounded-lg border border-background-200 bg-background-200/40 p-5">
        <h2 className="text-sm font-bold text-foreground-800 mb-3">热门赛事资讯推荐</h2>
        <div className="flex flex-wrap gap-2">
          {newsItems.slice(0, 5).map((item) => (
            <Link
              key={item.id}
              to={`/detail/${item.id}`}
              className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
            >
              {item.title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}